import { useState, useEffect, useMemo } from 'react';
import { format, addDays, differenceInCalendarDays } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { EnhancedCostCentreSelect } from '@/components/booking/EnhancedCostCentreSelect';
import { EnhancedMultiResourceSelect } from '@/components/booking/EnhancedMultiResourceSelect';
import { EnhancedProjectSelect } from '@/components/booking/EnhancedProjectSelect';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getProjectCostCentres, isCostCentreValidForProject } from '@/lib/project-cost-centres';
import { Booking, CostCentre, ProjectDetails, Resource, WorkingHoursProfile } from '@/types';
import { useScheduler } from '@/contexts/SchedulerContext';
import { toast } from 'sonner';
import { getNavMonths } from '@/lib/datePickerNav';

/**
 * Resolve where a booking lands when moved to `targetDate`, keeping the original
 * wall-clock times of day and the original span in calendar days. Working purely
 * with local date parts keeps the result stable across timezones and DST changes.
 */
export function getMovedRange(booking: Booking, targetDate: Date): { newStartDate: Date; newEndDate: Date } {
  const originalStart = new Date(booking.startTime);
  const originalEnd = new Date(booking.endTime);
  const daySpan = differenceInCalendarDays(originalEnd, originalStart);

  const newStartDate = new Date(targetDate);
  newStartDate.setHours(
    originalStart.getHours(),
    originalStart.getMinutes(),
    originalStart.getSeconds(),
    originalStart.getMilliseconds()
  );

  const newEndDate = addDays(newStartDate, daySpan);
  newEndDate.setHours(
    originalEnd.getHours(),
    originalEnd.getMinutes(),
    originalEnd.getSeconds(),
    originalEnd.getMilliseconds()
  );

  return { newStartDate, newEndDate };
}

interface MoveBookingDialogProps {
  open: boolean;
  onClose: () => void;
  booking: Booking | null;
  resources: Resource[];
  projects: ProjectDetails[];
  costCentres: CostCentre[];
  isProjectView: boolean;
  dataMode: 'demo' | 'live';
  onMove: (
    bookingId: string | number,
    newStart: Date,
    newEnd: Date,
    newResourceId: string,
    newProjectId: string | null,
    newCostCentreId: string | null
  ) => void;
  calculateUtilization?: (resourceId: string) => number;
  /**
   * The span to move when the booking is one piece of a split booking: the whole group's,
   * since the pieces move together. Defaults to the booking's own span.
   */
  movedSpan?: { startTime: string; endTime: string } | null;
  /**
   * Whether the booking's type skips non-working days and bank holidays. The server
   * re-plans such a move over the working days it lands on, so days off do not block it.
   */
  skipsNonWorkingDays?: boolean;
}

export function MoveBookingDialog({
  open,
  onClose,
  booking,
  resources,
  projects,
  costCentres,
  isProjectView,
  dataMode,
  onMove,
  calculateUtilization,
  movedSpan,
  skipsNonWorkingDays = false,
}: MoveBookingDialogProps) {
  const { settings } = useScheduler();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedResourceId, setSelectedResourceId] = useState<string>('');
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [selectedCostCentreId, setSelectedCostCentreId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const movedBooking = booking && movedSpan ? { ...booking, ...movedSpan } : booking;

  // Initialize form values when booking changes
  useEffect(() => {
    if (booking && open) {
      // Set current booking date - handle both startTime and startDate for compatibility
      try {
        const bookingDate = new Date(movedSpan?.startTime || booking.startTime || (booking as any).startDate);
        if (!isNaN(bookingDate.getTime())) {
          setSelectedDate(bookingDate);
        }
      } catch (error) {
        console.error('Error parsing booking date:', error);
      }
      
      // Set current resource
      setSelectedResourceId(booking.resourceId ? String(booking.resourceId) : '');
      setSelectedProjectId(booking.projectId != null ? String(booking.projectId) : '');
      setSelectedCostCentreId(booking.costCentreId != null ? String(booking.costCentreId) : '');
    }
  }, [booking, movedSpan?.startTime, open]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedDate(undefined);
      setSelectedResourceId('');
      setSelectedProjectId('');
      setSelectedCostCentreId('');
      setIsSubmitting(false);
      setIsDatePickerOpen(false);
    }
  }, [open]);

  // Handle date selection with automatic dismissal
  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    // Auto-close the date picker after selection
    if (date) {
      setTimeout(() => {
        setIsDatePickerOpen(false);
      }, 200);
    }
  };

  const handleMove = async () => {
    if (!booking || !movedBooking || !selectedDate || !selectedResourceId) return;

    // Validate that the selected date is a working day
    if (!skipsNonWorkingDays && !isWorkingDay(selectedDate)) {
      toast.error('Cannot move booking to a non-working day', {
        description: 'Please select a date that falls on a working day for this resource.',
      });
      return;
    }

    // Calculate the booking's start/end after moving
    const { newStartDate, newEndDate } = getMovedRange(movedBooking, selectedDate);
    
    // Check if end date falls on a working day
    if (!skipsNonWorkingDays && !isWorkingDay(newEndDate)) {
      const resourceName = selectedResource?.name || 'this resource';
      const workingDays = getWorkingDaysDisplay();
      const endDayName = getDayName(newEndDate);
      
      toast.error('Cannot move booking to this date', {
        description: `This booking would extend to ${endDayName}, which is outside ${resourceName}'s work schedule (${workingDays})`,
        duration: 5000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await onMove(
        booking.id,
        newStartDate,
        newEndDate,
        selectedResourceId,
        selectedProjectId || null,
        selectedCostCentreId || null
      );
      onClose();
    } catch (error) {
      console.error('Error moving booking:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedResource = resources.find(r => String(r.id) === selectedResourceId);
  const projectCostCentres = useMemo(
    () => getProjectCostCentres(selectedProjectId, projects, costCentres, dataMode),
    [selectedProjectId, projects, costCentres, dataMode],
  );

  // The move targets one resource, so the multi-select's array collapses to the
  // newly ticked id (or nothing when the current one is unticked).
  const handleResourceSelect = (resourceIds: string[]) => {
    const newId = resourceIds.filter(id => id !== selectedResourceId).pop() ?? '';
    setSelectedResourceId(newId);
  };

  const handleProjectSelect = (projectId: string) => {
    setSelectedProjectId(projectId);
    if (
      !projectId
      || !isCostCentreValidForProject(
        selectedCostCentreId,
        projectId,
        projects,
        costCentres,
        dataMode,
      )
    ) {
      setSelectedCostCentreId('');
    }
  };
  
  // Get the working profile for the selected resource
  const workingProfile = useMemo((): WorkingHoursProfile => {
    if (selectedResource?.useCustomWorkingProfile && selectedResource.customWorkingProfile) {
      return selectedResource.customWorkingProfile;
    }
    return settings.globalWorkingProfile;
  }, [selectedResource, settings.globalWorkingProfile]);

  // Function to check if a date is a working day
  const isWorkingDay = (date: Date): boolean => {
    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[dayOfWeek] as keyof WorkingHoursProfile;
    return workingProfile[dayName] > 0;
  };

  // Helper to get day name from date
  const getDayName = (date: Date): string => {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return dayNames[date.getDay()];
  };

  // Helper to get working days list for display
  const getWorkingDaysDisplay = (): string => {
    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const dayKeys: (keyof WorkingHoursProfile)[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    return dayNames
      .filter((_, index) => workingProfile[dayKeys[index]] > 0)
      .join(', ');
  };

  // Check if moving to this date would cause booking to extend beyond working days
  const wouldExtendBeyondWorkingDays = (date: Date): boolean => {
    if (!movedBooking) return false;
    
    try {
      const { newEndDate } = getMovedRange(movedBooking, date);
      return !isWorkingDay(newEndDate);
    } catch {
      return false;
    }
  };

  // Function to disable non-working days in the calendar
  const disabledDates = (date: Date): boolean => {
    // A booking type that skips non-working days is re-planned around them by the server.
    if (skipsNonWorkingDays) return false;

    // Disable if the start date is not a working day
    if (!isWorkingDay(date)) return true;
    
    // Disable if the booking would extend beyond working days
    if (wouldExtendBeyondWorkingDays(date)) return true;
    
    return false;
  };
  
  const bookingStartDate = (() => {
    if (!booking) return undefined;
    const parsed = new Date(movedSpan?.startTime || booking.startTime || (booking as any).startDate);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  })();

  // Check if there are any changes from the original booking
  const hasChanges = booking && (() => {
    try {
      const originalDate = new Date(movedSpan?.startTime || booking.startTime || (booking as any).startDate);
      const dateChanged = selectedDate && !isNaN(originalDate.getTime()) && 
        selectedDate.toDateString() !== originalDate.toDateString();
      const resourceChanged = selectedResourceId !== String(booking.resourceId);
      const projectChanged = isProjectView
        && selectedProjectId !== String(booking.projectId ?? '');
      const costCentreChanged = isProjectView
        && selectedCostCentreId !== String(booking.costCentreId ?? '');
      return dateChanged || resourceChanged || projectChanged || costCentreChanged;
    } catch {
      return false;
    }
  })();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Move Booking</DialogTitle>
          <DialogDescription>
            {isProjectView
              ? 'Change the date, resource, project and/or cost centre for this booking.'
              : 'Change the date and/or resource for this booking.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Date Picker */}
          <div className="space-y-2">
            <Label htmlFor="date">Date</Label>
            <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  id="date"
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !selectedDate && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {selectedDate ? format(selectedDate, 'PPP') : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  defaultMonth={selectedDate ?? bookingStartDate ?? new Date()}
                  onSelect={handleDateSelect}
                  disabled={disabledDates}
                  captionLayout="dropdown"
                  {...getNavMonths(selectedDate ?? bookingStartDate)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Resource Selector */}
          <div className="space-y-2">
            <Label htmlFor="resource">Resource</Label>
            <EnhancedMultiResourceSelect
              resources={resources}
              selectedResourceIds={selectedResourceId ? [selectedResourceId] : []}
              onSelect={handleResourceSelect}
              calculateUtilization={calculateUtilization}
              utilisationTiers={settings?.utilisationTiers}
              placeholder="Select resource..."
            />
          </div>

          {isProjectView && (
            <>
              <div className="space-y-2">
                <Label>Project</Label>
                <EnhancedProjectSelect
                  projects={projects}
                  selectedProjectId={selectedProjectId}
                  onSelect={handleProjectSelect}
                  placeholder="Select project..."
                />
              </div>

              <div className="space-y-2">
                <Label>Cost Centre</Label>
                <EnhancedCostCentreSelect
                  costCentres={projectCostCentres}
                  allCostCentres={costCentres}
                  selectedCostCentreId={selectedCostCentreId}
                  onSelect={setSelectedCostCentreId}
                  disabled={!selectedProjectId}
                  placeholder={selectedProjectId
                    ? 'Select cost centre...'
                    : 'Select a project first...'}
                  projectId={selectedProjectId}
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleMove}
            disabled={!selectedDate || !selectedResourceId || !hasChanges || isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Move Booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
